import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHead, EASE } from "@/components/Reveal";
import { AssessmentButton, GhostLink } from "@/components/Ctas";
import { CALENDLY_URL } from "@/config";

const MONO = { fontFamily: "'JetBrains Mono', monospace" };

function Pill({ cx, cy, w, label, dark = false }) {
  return (
    <g>
      <rect
        x={cx - w / 2}
        y={cy - 19}
        width={w}
        height={38}
        rx={19}
        fill={dark ? "#0F172A" : "#FAF8F5"}
        stroke="#0F172A"
        strokeOpacity={dark ? 0 : 0.35}
        strokeWidth="1.2"
      />
      <text
        x={cx}
        y={cy + 3}
        textAnchor="middle"
        fill={dark ? "#FAF8F5" : "#0F172A"}
        fontSize="9.5"
        letterSpacing="1.5"
        style={MONO}
      >
        {label}
      </text>
    </g>
  );
}

function Layer({ delay, children }) {
  return (
    <motion.g
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.g>
  );
}

export function EngagementModel({ className = "" }) {
  return (
    <svg
      data-testid="engagement-model"
      viewBox="0 0 520 660"
      fill="none"
      className={className}
      role="img"
      aria-label="Engagement model: Assessment or Intro Call converge into Assess, then Clarify, then three paths — Strategy Sprint, Workshop, Consulting — then the shared methodology Prioritize, Build, Learn, Adapt, with Adapt looping back to Assess"
    >
      {/* layer captions */}
      <text x="260" y="30" textAnchor="middle" fill="#64748B" fontSize="10" letterSpacing="2.5" style={MONO}>ENTRY</text>
      <text x="20" y="282" fill="#64748B" fontSize="10" letterSpacing="2.5" style={MONO}>CHOOSE YOUR PATH</text>
      <text x="20" y="445" fill="#64748B" fontSize="10" letterSpacing="2.5" style={MONO}>SHARED METHODOLOGY</text>

      {/* ENTRY */}
      <Layer delay={0.1}>
        <Pill cx={150} cy={64} w={150} label="FREE ASSESSMENT" />
        <Pill cx={370} cy={64} w={120} label="INTRO CALL" />
        <path d="M150 84 C 165 112, 215 120, 238 130" stroke="#0F172A" strokeOpacity="0.3" className="dash-flow" />
        <path d="M370 84 C 355 112, 305 120, 282 130" stroke="#0F172A" strokeOpacity="0.3" className="dash-flow" />
      </Layer>

      {/* ASSESS */}
      <Layer delay={0.4}>
        <text x="196" y="150" textAnchor="end" fill="#C86D3B" fontSize="10" letterSpacing="2" style={MONO}>01</text>
        <Pill cx={260} cy={146} w={110} label="ASSESS" dark />
        <path d="M260 166 L260 206" stroke="#0F172A" strokeOpacity="0.35" strokeWidth="1.5" className="dash-flow" />
      </Layer>

      {/* CLARIFY */}
      <Layer delay={0.7}>
        <text x="196" y="229" textAnchor="end" fill="#C86D3B" fontSize="10" letterSpacing="2" style={MONO}>02</text>
        <Pill cx={260} cy={225} w={110} label="CLARIFY" />
      </Layer>

      {/* CHOOSE YOUR PATH */}
      <Layer delay={1.0}>
        <path d="M260 244 C 240 292, 150 292, 95 311" stroke="#0F172A" strokeOpacity="0.3" className="dash-flow" />
        <path d="M260 244 L260 311" stroke="#0F172A" strokeOpacity="0.3" className="dash-flow" />
        <path d="M260 244 C 280 292, 370 292, 425 311" stroke="#0F172A" strokeOpacity="0.3" className="dash-flow" />
        <Pill cx={95} cy={330} w={160} label="STRATEGY SPRINT" />
        <Pill cx={260} cy={330} w={120} label="WORKSHOP" />
        <Pill cx={425} cy={330} w={130} label="CONSULTING" />
      </Layer>

      {/* SHARED METHODOLOGY */}
      <Layer delay={1.3}>
        <path d="M95 350 C 95 402, 200 410, 248 424" stroke="#0F172A" strokeOpacity="0.25" className="dash-flow" />
        <path d="M260 350 L260 424" stroke="#0F172A" strokeOpacity="0.25" className="dash-flow" />
        <path d="M425 350 C 425 402, 320 410, 272 424" stroke="#0F172A" strokeOpacity="0.25" className="dash-flow" />
        <circle cx="260" cy="427" r="4" fill="#0F172A" />
        <path d="M260 431 L260 470" stroke="#0F172A" strokeOpacity="0.35" strokeWidth="1.5" className="dash-flow" />
        <path d="M80 470 L420 470" stroke="#C86D3B" strokeOpacity="0.4" strokeDasharray="3 7" />
        {[
          { x: 80, label: "03 · PRIORITIZE" },
          { x: 200, label: "04 · BUILD" },
          { x: 320, label: "05 · LEARN" },
        ].map((n) => (
          <g key={n.label}>
            <circle cx={n.x} cy="470" r="7" fill="#0F172A" />
            <text x={n.x} y="496" textAnchor="middle" fill="#0F172A" fontSize="9" letterSpacing="1.5" style={MONO}>
              {n.label}
            </text>
          </g>
        ))}
        <circle cx="420" cy="470" r="7" fill="#C86D3B" className="pulse-dot" />
        <text x="420" y="496" textAnchor="middle" fill="#C86D3B" fontSize="9" letterSpacing="1.5" style={MONO}>
          06 · ADAPT
        </text>
      </Layer>

      {/* loop back */}
      <Layer delay={1.7}>
        <path
          d="M420 478 C 508 500, 514 350, 496 250 C 482 172, 380 118, 302 138"
          stroke="#C86D3B"
          strokeOpacity="0.5"
          strokeDasharray="4 6"
        />
        <circle r="4" fill="#C86D3B">
          <animateMotion
            dur="9s"
            repeatCount="indefinite"
            path="M420 478 C 508 500, 514 350, 496 250 C 482 172, 380 118, 302 138"
          />
        </circle>
      </Layer>
    </svg>
  );
}

const STEPS = [
  {
    n: "01",
    name: "ASSESS",
    desc: "Two entry points — the free assessment or an intro call. Both lead to the same place: understanding the current situation, challenge and context.",
  },
  {
    n: "02",
    name: "CLARIFY",
    desc: "What are we actually trying to solve? The assessment or conversation identifies the real challenge and the desired outcome.",
  },
  {
    n: "",
    name: "CHOOSE THE RIGHT PATH",
    desc: "Strategy Sprint, Workshop or Consulting — guided by your situation, not a predetermined package.",
  },
  {
    n: "03–06",
    name: "SHARED METHODOLOGY",
    desc: "Prioritize, build, learn, adapt. The same operating rhythm behind every engagement — and Adapt leads back into the cycle, because strategy is not a one-time event.",
  },
];

const PATHS = [
  {
    label: "Strategy Sprint",
    title: "From uncertainty to direction.",
    copy: "Focused, structured work to clarify the problem, align priorities and determine the right direction.",
    cta: "Start with a Strategy Sprint",
    to: "/contact?type=strategy-sprint",
    testid: "hiw-path-sprint",
  },
  {
    label: "Workshop",
    title: "Build capability around a known challenge.",
    copy: "Focused collaborative work to learn, align, explore or solve a specific challenge.",
    cta: "Discuss a Workshop",
    to: "/contact?type=workshop",
    testid: "hiw-path-workshop",
  },
  {
    label: "Consulting",
    title: "Deeper, sustained support.",
    copy: "Deeper strategic or ongoing support when the challenge requires more sustained involvement.",
    cta: "Get in Touch",
    to: "/contact",
    testid: "hiw-path-consulting",
  },
];

const METHOD = ["ASSESS", "CLARIFY", "PRIORITIZE", "BUILD", "LEARN", "ADAPT"];

const pillCls =
  "group inline-flex items-center gap-2.5 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold tracking-wide text-paper transition-colors duration-300 hover:bg-amber";

export default function HowItWorks() {
  useEffect(() => {
    document.title = "How It Works — KP Strategy";
    return () => {
      document.title =
        "KP Strategy — Design your business like a product. Run it like a system. Scale it with AI.";
    };
  }, []);

  return (
    <main data-testid="how-it-works-page">
      {/* Hero */}
      <section className="pt-[72px]" data-testid="hiw-hero">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pt-24 lg:px-8 lg:pb-20 lg:pt-28">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber">
              <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
              How It Works
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-7 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
              How We Work Together
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-body sm:text-lg">
              Every engagement starts the same way: understanding where you
              are. From there, we choose the right path together — a Strategy
              Sprint, a Workshop or deeper Consulting — guided by one shared
              methodology.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <AssessmentButton testid="hiw-cta-assessment" dark={false} />
              <GhostLink testid="hiw-cta-book-call" href={CALENDLY_URL}>
                Book an Intro Call
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* The Model */}
      <section className="border-t border-line bg-parch py-20 sm:py-28" data-testid="hiw-model">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="01 — The Model"
            title="Two entry points. Three paths. One methodology."
            copy="First we understand your situation. Then we choose the right way to work together. The methodology underneath stays the same."
            testid="hiw-model-head"
          />
          <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-7">
              <EngagementModel className="mx-auto w-full max-w-[520px]" />
            </Reveal>
            <div className="lg:col-span-5">
              <ol className="space-y-8">
                {STEPS.map((s, i) => (
                  <Reveal key={s.n} delay={0.1 + i * 0.08}>
                    <li className="border-t border-ink/10 pt-5" data-testid={`hiw-step-${i + 1}`}>
                      {s.n && (
                        <p className="font-mono text-[11px] text-amber">{s.n}</p>
                      )}
                      <p className="mt-1.5 font-mono text-sm font-medium uppercase tracking-[0.15em] text-ink">
                        {s.name}
                      </p>
                      <p className="mt-2.5 text-sm leading-relaxed text-body sm:text-base">
                        {s.desc}
                      </p>
                    </li>
                  </Reveal>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Choose the right path */}
      <section className="border-t border-line py-20 sm:py-28" data-testid="hiw-paths">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="02 — The Paths"
            title="Choose the right path for where you are."
            copy="The engagement is selected based on your situation — then the methodology guides the work."
            testid="hiw-paths-head"
          />
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {PATHS.map((p, i) => (
              <Reveal key={p.label} delay={i * 0.1} className="h-full">
                <div
                  data-testid={p.testid}
                  className="flex h-full flex-col border border-line bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber/60 sm:p-10"
                >
                  <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-amber">
                    {p.label}
                  </p>
                  <h3 className="mt-4 font-serif text-2xl font-normal text-ink">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-body sm:text-base">
                    {p.copy}
                  </p>
                  <div className="mt-auto pt-8">
                    <Link
                      data-testid={`${p.testid}-cta`}
                      to={p.to}
                      className="group inline-flex items-center gap-2 border-b border-ink/30 pb-1 text-sm font-semibold tracking-wide text-ink transition-colors duration-300 hover:border-amber hover:text-amber"
                    >
                      {p.cta}
                      <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-line bg-parch py-20 sm:py-28" data-testid="hiw-methodology">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="03 — One Methodology"
            title="Built on one methodology."
            copy="Assess and Clarify shape the engagement. The same six steps then guide the work — whatever path we take."
            testid="hiw-methodology-head"
          />
          <Reveal delay={0.15}>
            <div className="mt-14 border border-ink/15 bg-paper px-6 py-8 sm:px-10">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-faint">
                Built on one methodology
              </p>
              <ol className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-0" data-testid="hiw-methodology-strip">
                {METHOD.map((step, i) => (
                  <li key={step} className="flex items-center">
                    <span className="flex items-baseline gap-2">
                      <span className="font-mono text-[11px] text-amber">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-mono text-sm font-medium uppercase tracking-[0.15em] text-ink">
                        {step}
                      </span>
                    </span>
                    {i < METHOD.length - 1 && (
                      <span className="hidden px-4 text-ink/30 sm:inline" aria-hidden="true">
                        →
                      </span>
                    )}
                  </li>
                ))}
              </ol>
              <p className="mt-6 border-t border-line pt-5 text-sm leading-relaxed text-body">
                Adapt isn&apos;t an endpoint — it loops back into Assess, so
                the work evolves as your organization, market and needs evolve.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-ink py-24 text-paper sm:py-32" data-testid="hiw-final-cta">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber">
              04 — Next Step
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl md:text-5xl">
              Get Your Free Strategy &amp; Systems Assessment
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-paper/70 sm:text-lg">
              Identify where you&apos;re losing clarity, capacity or momentum —
              and what to focus on next.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-7">
              <AssessmentButton testid="hiw-final-cta-assessment" dark />
              <GhostLink testid="hiw-final-cta-book-call" href={CALENDLY_URL} dark>
                Book an Intro Call
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
